import type { DesignTokens } from "../types/theme";

// FamiDex brand font: Literata (serif, bundled in apps/web), with a robust
// system serif fallback chain so text stays readable if the webfont is slow/offline.
const WEB_SYSTEM_FONT = "'Literata', ui-serif, Georgia, Cambria, 'Times New Roman', serif";

/**
 * Base tokens for Light mode.
 */
export const LIGHT_BASE_TOKENS: DesignTokens = {
  background: "#f8faf6",
  foreground: "#1b3022",
  surface: "#ffffff",
  surfaceForeground: "#1b3022",
  surfaceSecondary: "#f2f4f0",
  surfaceSecondaryForeground: "#1b3022",
  surfaceTertiary: "#d8dbd7",
  surfaceTertiaryForeground: "#1b3022",
  overlay: "#ffffff",
  overlayForeground: "#1b3022",
  // Verde-gris "Forest Nurture" (≈ oklch(0.45 0.03 154) de ui/vars.css) — nunca
  // el zinc neutro del template, que dejaba texto secundario y carriles grises.
  muted: "#54685c",
  default: "#e8f3e9",
  defaultForeground: "#1b3022",
  accent: "#2d6a4f",
  accentForeground: "#ffffff",
  danger: "#bc4749",
  dangerForeground: "#ffffff",
  warning: "#f59e0b",
  warningForeground: "#1b3022",
  success: "#2d6a4f",
  successForeground: "#f8faf6",
  separator: "#d8dbd7",
  glassBackground: "rgba(255, 255, 255, 0.85)",
  glassBorder: "rgba(0, 0, 0, 0.1)",
  glassHighlight: "rgba(0, 0, 0, 0.08)",
  glow0: "rgba(45, 106, 79, 0.12)",
  glow1: "rgba(60, 140, 100, 0.1)",
  glow2: "rgba(149, 213, 178, 0.08)",
  mutedForeground: "#1b3022",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(0, 0, 0, 0.1)",
  overlayShadow: "rgba(0, 0, 0, 0.1)",
  fieldShadow: "rgba(0, 0, 0, 0.1)",
  fieldBorder: "rgba(0, 0, 0, 0.1)",
  fieldPlaceholder: "rgba(0, 0, 0, 0.4)",
  fieldBackground: "rgba(0, 0, 0, 0.03)",
  segment: "#ffffff",
  segmentForeground: "#1b3022",
  link: "#1b3022",
  focus: "#2d6a4f",
  fieldForeground: "#1b3022",
  radius: "0.65rem",
  fontNormal: WEB_SYSTEM_FONT,
  fontMedium: WEB_SYSTEM_FONT,
  fontSemiBold: WEB_SYSTEM_FONT,
  fontBold: WEB_SYSTEM_FONT,
  border: "#d8dbd7",
  primary: "#2d6a4f",
  primaryForeground: "#ffffff",
  secondary: "#e8f3e9",
  secondaryForeground: "#1b3022",
  backdrop: "rgba(0, 0, 0, 0.5)",
};

/**
 * Base tokens for Dark mode.
 */
export const DARK_BASE_TOKENS: DesignTokens = {
  background: "#152018",
  foreground: "#f8faf6",
  surface: "#1b3022",
  surfaceForeground: "#f8faf6",
  surfaceSecondary: "#2d3a30",
  surfaceSecondaryForeground: "#f8faf6",
  surfaceTertiary: "#405144",
  surfaceTertiaryForeground: "#f8faf6",
  overlay: "#1b3022",
  overlayForeground: "#f8faf6",
  muted: "#7a9885",
  default: "#2d3a30",
  defaultForeground: "#f8faf6",
  accent: "#95d5b2",
  accentForeground: "#1b3022",
  danger: "#bc4749",
  dangerForeground: "#ffffff",
  warning: "#e29578",
  warningForeground: "#1b3022",
  success: "#2d6a4f",
  successForeground: "#1b3022",
  separator: "#405144",
  glassBackground: "rgba(10, 10, 10, 0.985)",
  glassBorder: "rgba(255, 255, 255, 0.12)",
  glassHighlight: "rgba(255, 255, 255, 0.25)",
  glow0: "rgba(149, 213, 178, 0.5)",
  glow1: "rgba(60, 140, 100, 0.45)",
  glow2: "rgba(149, 213, 178, 0.35)",
  mutedForeground: "#f8faf6",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(0, 0, 0, 0.1)",
  overlayShadow: "rgba(0, 0, 0, 0.1)",
  fieldShadow: "rgba(0, 0, 0, 0.1)",
  fieldBorder: "rgba(255, 255, 255, 0.1)",
  fieldPlaceholder: "rgba(255, 255, 255, 0.4)",
  fieldBackground: "rgba(255, 255, 255, 0.03)",
  segment: "#2d3a30",
  segmentForeground: "#f8faf6",
  link: "#f8faf6",
  focus: "#95d5b2",
  fieldForeground: "#f8faf6",
  radius: "0.65rem",
  fontNormal: WEB_SYSTEM_FONT,
  fontMedium: WEB_SYSTEM_FONT,
  fontSemiBold: WEB_SYSTEM_FONT,
  fontBold: WEB_SYSTEM_FONT,
  border: "#405144",
  primary: "#95d5b2",
  primaryForeground: "#1b3022",
  secondary: "#2d3a30",
  secondaryForeground: "#f8faf6",
  backdrop: "rgba(0, 0, 0, 0.7)",
};
